import ResultsFormatterBase from "../../formatters/ResultsFormatterBase.js";
import BaseballResultsProcessor from '../../entities/BaseballResultsProcessor.js';
import { 
    ResultData, ResultType,
    HitReprocessed, PitchReprocessed, PitchData, DataSection, ResultDataNew, ScoringData,
} from "../../enums/index.js";
import classifiesAsPlateAppearance from "../../classificators/plateAppearanceResult/classifiesAsPlateAppearance.js";
import IterableDataProcessor from "../../entities/IterableDataProcessor.js";

class PlateAppearanceFormatter extends ResultsFormatterBase {
    constructor(newStructure, plateAppearanceClassifier) {
        super();

        if (typeof(newStructure) === 'undefined') {
            this.newStructure = false;
        }
        else if(typeof(newStructure) === 'boolean') {
            this.newStructure = newStructure;
        }
        else {
            throw new TypeError('newStructure must be boolean or undefined');
        }

        
        if (typeof(plateAppearanceClassifier) === 'undefined') {
            this.plateAppearanceClassifier = classifiesAsPlateAppearance;
        }
        else if(typeof(plateAppearanceClassifier) === 'function') {
            this.plateAppearanceClassifier = plateAppearanceClassifier;
        }
        else {
            throw new TypeError('plateAppearanceClassifier must be an predicate taking PlateAppearance Enum key or undefined to use default');
        }
    }

    formatNewStructure(results) {
        const dataKey = ResultData.mapPath(ResultData.Data.key);
        const typeKey = ResultData.mapPath(ResultData.Type.key);
        const propertyPA = PitchReprocessed.PlateAppearanceR.key;
        const propertyPofPA = PitchReprocessed.PitchOfPlateAppearanceR.key;

        let paPerBatter = {};
        let previousBatterId = '';

        for (let i = 0; i < results.length; i++) {
            let result = results[i];

            if (!result || !result.hasOwnProperty(dataKey) || !result[dataKey]) {
                continue;
            }

            if (!ResultType.hasPitch(result[typeKey])) {
                continue;
            }

            if (
                !result[dataKey].hasOwnProperty(DataSection.Scoring.key)
                || result[dataKey][DataSection.Scoring.key] === null
            ) {
                result[dataKey][DataSection.Scoring.key] = {};
            }

            let uniquePlayerId = '';

            uniquePlayerId = this.determinePlayerIdFromPitch(result);

            if (uniquePlayerId) {
                if (!paPerBatter.hasOwnProperty(uniquePlayerId)) {
                    paPerBatter[uniquePlayerId] = this.getDefaultPaCounter();
                }

                if (
                    result[dataKey][DataSection.Scoring.key].hasOwnProperty(PitchData.AfterPlayPlateAppearance.key)
                    && !isNaN(result[dataKey][DataSection.Scoring.key][PitchData.AfterPlayPlateAppearance.key])
                    && result[dataKey][DataSection.Scoring.key][PitchData.AfterPlayPlateAppearance.key] >= 0
                ) {
                    // prefer scoring data
                    if (paPerBatter[uniquePlayerId].PA != result[dataKey][DataSection.Scoring.key][PitchData.AfterPlayPlateAppearance.key]) {
                        paPerBatter[uniquePlayerId].PofPA = 0;
                    }
                    paPerBatter[uniquePlayerId].PA = result[dataKey][DataSection.Scoring.key][PitchData.AfterPlayPlateAppearance.key];
                }
                else {
                    const paResult = IterableDataProcessor.getObjectContent(
                        result, ResultDataNew.getDataPath(ScoringData.PlateAppearanceResult.key, DataSection.Scoring.key)
                    );
                    if (paResult) {
                        // determine from pa result, better and more reliable way of doing that
                        if (this.plateAppearanceClassifier(paResult)) {
                            paPerBatter[uniquePlayerId].PA ++;
                            paPerBatter[uniquePlayerId].PofPA = 0;
                        }
                    }
                    else if (previousBatterId !== uniquePlayerId){
                        paPerBatter[uniquePlayerId].PA ++;
                        paPerBatter[uniquePlayerId].PofPA = 0;
                    }
                }

                paPerBatter[uniquePlayerId].PofPA ++;

                result[dataKey][DataSection.Scoring.key][propertyPA] = paPerBatter[uniquePlayerId].PA;
                result[dataKey][DataSection.Scoring.key][propertyPofPA] = paPerBatter[uniquePlayerId].PofPA;
            }
            else {
                result[dataKey][DataSection.Scoring.key][propertyPA] = null;
                result[dataKey][DataSection.Scoring.key][propertyPofPA] = null;
            }

            previousBatterId = uniquePlayerId;
        }

        return results;
    }

    format(results) {
        if (this.newStructure) {
            return this.formatNewStructure(results);
        }

        let paPerBatter = {};
        let previousBatterId = '';

        for (let i = 0; i < results.length; i++){
            let result = results[i];

            if (!result || !result.hasOwnProperty(ResultData.Data.key) || !result[ResultData.Data.key]){
                continue;
            }

            let uniquePlayerId = '';
            let isHit = false;

            // TODO - separate methods?
            switch (result[ResultData.Type.key]) {
                case ResultType.Pitch.key:
                    uniquePlayerId = this.determinePlayerIdFromPitch(result);
                    break;
                case ResultType.Hit.key:
                    isHit = true;
                    // check if there is related pitch...
                    let relatedPitch = BaseballResultsProcessor.getRelatedResult(result[ResultData.PitchId.key], results);
                    if (
                        relatedPitch && relatedPitch[ResultData.Data.key]
                        && relatedPitch[ResultData.Data.key][PitchReprocessed.PlateAppearanceR.key]
                        && relatedPitch[ResultData.Data.key][PitchReprocessed.PitchOfPlateAppearanceR.key]
                    ) {
                        result[ResultData.Data.key][HitReprocessed.PlateAppearanceR.key] = relatedPitch[ResultData.Data.key][PitchReprocessed.PlateAppearanceR.key];
                        result[ResultData.Data.key][HitReprocessed.PitchOfPlateAppearanceR.key] = relatedPitch[ResultData.Data.key][PitchReprocessed.PitchOfPlateAppearanceR.key];
                        continue;
                    }

                    // now we need to analyze and count it, as it looks like we have no pitch for this hit...
                    uniquePlayerId = this.determinePlayerIdFromHit(result);

                    break;
            
                default:
                    continue;
            }

            const propertyPA = isHit ? HitReprocessed.PlateAppearanceR.key : PitchReprocessed.PlateAppearanceR.key;
            const propertyPofPA = isHit ? HitReprocessed.PitchOfPlateAppearanceR.key : PitchReprocessed.PitchOfPlateAppearanceR.key;

            if (uniquePlayerId) {
                if (!paPerBatter.hasOwnProperty(uniquePlayerId)) {
                    paPerBatter[uniquePlayerId] = this.getDefaultPaCounter();
                }

                if (
                    !isHit 
                    && result[ResultData.Data.key].hasOwnProperty(PitchData.AfterPlayPlateAppearance.key)
                    && !isNaN(result[ResultData.Data.key][PitchData.AfterPlayPlateAppearance.key])
                    && result[ResultData.Data.key][PitchData.AfterPlayPlateAppearance.key] > 0
                ) {
                    // prefer scoring data
                    if (paPerBatter[uniquePlayerId].PA != result[ResultData.Data.key][PitchData.AfterPlayPlateAppearance.key]) {
                        paPerBatter[uniquePlayerId].PofPA = 0;
                    }
                    paPerBatter[uniquePlayerId].PA = result[ResultData.Data.key][PitchData.AfterPlayPlateAppearance.key];
                }
                else {
                    if (previousBatterId !== uniquePlayerId){
                        paPerBatter[uniquePlayerId].PA ++;
                        paPerBatter[uniquePlayerId].PofPA = 0;
                    }
                }

                paPerBatter[uniquePlayerId].PofPA ++;

                result[ResultData.Data.key][propertyPA] = paPerBatter[uniquePlayerId].PA;
                result[ResultData.Data.key][propertyPofPA] = paPerBatter[uniquePlayerId].PofPA;
            }
            else {
                result[ResultData.Data.key][propertyPA] = null;
                result[ResultData.Data.key][propertyPofPA] = null;
            }

            previousBatterId = uniquePlayerId;
        }

        return results;
    }

    getTagId(id){
        return `tag-${id}`;
    }

    getPlayerId(id){
        return `player-${id}`;
    }

    determinePlayerIdFromPitch(result) {
        const sessionIdProp = this.newStructure ? ResultDataNew.SessionId.key : ResultData.SessionId.key;
        const tagIdProp = this.newStructure ? ResultDataNew.BatterId.key : ResultData.BatterTagId.key;

        let uniquePlayerId = result.hasOwnProperty(sessionIdProp) ? result[sessionIdProp] + '-': '';

        if (result.hasOwnProperty(tagIdProp) && result[tagIdProp] != 0) {
            uniquePlayerId += this.getTagId(result[tagIdProp]);
        }
        else if (result.hasOwnProperty(ResultData.BatterPlayerId.key) && result[ResultData.BatterPlayerId.key] != 0) {
            uniquePlayerId += this.getPlayerId(result[ResultData.BatterPlayerId.key]);
        }
        else {
            return null;
        }

        return uniquePlayerId;
    }

    getDefaultPaCounter(){
        return {
            PA: 0,
            PofPA: 0
        };
    }

    determinePlayerIdFromHit(result) {
        let uniquePlayerId = result.hasOwnProperty(ResultData.SessionId.key) ? result[ResultData.SessionId.key] + '-': '';

        if (result.hasOwnProperty(ResultData.TagId.key) && result[ResultData.TagId.key] != 0) {
            uniquePlayerId += this.getTagId(result[ResultData.TagId.key]);
        }
        else if (result.hasOwnProperty(ResultData.PlayerId.key) && result[ResultData.PlayerId.key] != 0) {
            uniquePlayerId += this.getPlayerId(result[ResultData.PlayerId.key]);
        }
        else {
            return null;
        }

        return uniquePlayerId;
    }
};

export default PlateAppearanceFormatter;